import { base64Decode } from '@firebase/util';
import { assertValid, checkEqual, checkPresent, isDevContext } from '@yp/common/core.ts';
import {
  isAbsent,
  isNumber,
  isObject,
  isPresent,
  isString,
  pare,
  parseJson,
  pick,
} from '@yp/common/identity.ts';
import { DescriptorCache } from '@yp/common/schema/cache.ts';
import { settableLoadable, type Loadable } from '@yp/util.svelte/loadable.ts';
import { BROWSER, DEV } from 'esm-env';
import type { Analytics, logEvent } from 'firebase/analytics';
import type { FirebaseApp, FirebaseOptions } from 'firebase/app';
import { initializeRecaptchaConfig, type Auth, type User } from 'firebase/auth';
import { CollectionReference, getDocs, type Firestore, type LogLevel } from 'firebase/firestore';
import type { fireEvent } from './analytics.ts';
import { customClaims } from './auth.ts';
import { FirestoreCache } from './cache.ts';
export type { fireEvent };

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean;
  }
}

export type FireEvent = typeof fireEvent;

export type FireContext = {
  app: FirebaseApp;
  auth: Auth;
  // This is to propagate to RecaptchaVerifier. See if there's a better way.
  // recaptchaSiteKey?: string;
  store: Firestore;
  // Innoculously named. This will be env if we should behave like we're in prod.
  env: boolean;
  // analytics: Analytics;
  emailDomain: string;
};

export type SessionType = {
  user: User | null;
  claims: Record<string, unknown>;
  // user: {
  //   name: string;
  //   email: string;
  //   emailVerified: boolean;
  //   uid: string;
  //   user: User;
  // } | null;
};

export type CompleteSignInWithEmailResult = {
  result: 'nolink' | 'noemail' | 'badcode' | 'success' | `error:${string}`;
  continueUrl?: string;
};

let analytics_: Analytics | undefined;
let logEvent_: typeof logEvent | undefined;

function wrap(loadable: Loadable<FireContext>) {
  async function providerFor(name: string) {
    const { GoogleAuthProvider } = await import('firebase/auth');
    const context = await loadable.load();
    switch (name) {
      case 'google': {
        const result = new GoogleAuthProvider();
        return result.setCustomParameters({
          hd: context.emailDomain,
          prompt: 'select_account',
        });
      }
      default:
        throw new Error(`unknown provider ${name}`);
    }
  }

  async function signInWith(name: string) {
    // https://firebase.google.com/docs/auth/web/redirect-best-practices#signinwithpopup
    // const { signInWithRedirect: signIn } = await import('firebase/auth');
    const { signInWithPopup: signIn } = await import('firebase/auth');
    const provider = await providerFor(name);
    const context = await loadable.load();
    await signIn(context.auth, provider);
  }

  async function signInWithToken(token: string) {
    const { signInWithCustomToken } = await import('firebase/auth');
    const context = await loadable.load();
    await signInWithCustomToken(context.auth, token);
  }

  async function signInWithEmail(email: string, returnToUrl?: string) {
    const { sendSignInLinkToEmail } = await import('firebase/auth');
    const context = await loadable.load();
    const url = returnToUrl ?? window.location.href;
    await sendSignInLinkToEmail(context.auth, email, { url, handleCodeInApp: true });
    // The link was successfully sent. Inform the user.
    // Save the email locally so you don't need to ask the user for it again
    // if they open the link on the same device.
    globalThis.localStorage.setItem('emailForSignIn', email);
    // notifications.showSuccess(`Check your email to complete login.`);
  }

  async function completeSignInWithEmail(email?: string): Promise<CompleteSignInWithEmailResult> {
    const { isSignInWithEmailLink, signInWithEmailLink } = await import('firebase/auth');
    const context = await loadable.load();
    if (!isSignInWithEmailLink(context.auth, window.location.href)) {
      return { result: 'nolink' };
    }
    email ??= globalThis.localStorage.getItem('emailForSignIn') ?? undefined;
    if (!isPresent(email)) {
      return { result: 'noemail' };
    }
    try {
      const url = new URL(window.location.href);
      const continueUrl = url.searchParams.get('continueUrl') ?? undefined;
      url.searchParams.delete('continueUrl');
      const _result = await signInWithEmailLink(context.auth, email, url.toString());
      // You can access the new user by importing getAdditionalUserInfo
      // and calling it with result:
      // getAdditionalUserInfo(result)
      // You can access the user's profile via:
      // getAdditionalUserInfo(result)?.profile
      // You can check if the user is new or existing:
      // getAdditionalUserInfo(result)?.isNewUser
      return pare({ result: 'success', continueUrl } as const);
    } catch (error) {
      const code =
        isObject(error) && 'code' in error && (isString(error.code) || isNumber(error.code))
          ? error.code
          : 'Unknown';
      console.error('Sign-in with email failed:', code);
      if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code') {
        return { result: 'badcode' };
      }
      return { result: `error:${code}` };
    } finally {
      // Clear email from storage.
      globalThis.localStorage.removeItem('emailForSignIn');
    }
  }

  async function signOut() {
    globalThis.localStorage.removeItem('emailForSignIn');
    const { signOut } = await import('firebase/auth');
    const context = await loadable.load();
    await signOut(context.auth);
  }

  const fire: FireEvent = (eventName, eventParams) => {
    if (analytics_) {
      // TODO: Not sure why this doesn't match the type.
      logEvent_?.(analytics_, eventName as string, eventParams);
    }
  };

  return {
    ...loadable,
    signInWith,
    signInWithToken,
    signInWithEmail,
    completeSignInWithEmail,
    signOut,
    // log: log as typeof LogEventType,
    fire,
  };
}

const fireContext = settableLoadable<FireContext>(undefined, 'FC');
export const FireContext = wrap(fireContext.loadable);

// TODO: Make undefined in type declaration unnecessary.
const session = settableLoadable<SessionType | undefined>(undefined, 'S');
export const Session = session.loadable;

function parseTokenClaims(idToken: string) {
  // Borrowed from _parseToken in @firebase/auth implementation of getIdTokenResult().
  try {
    const [_algorithm, payload, _signature] = idToken.split('.');
    // N.B. This is implemented more efficiently than base64ToString(payload, 'urlSafe'), but we
    // should probably converge on one implementation at some point.
    const decoded = checkPresent(base64Decode(payload));
    const claims = parseJson(decoded);
    assertValid(claims, isObject);
    return customClaims(claims);
  } catch (err) {
    console.error('getTokenClaims error', err);
    return {};
  }
}

export type OnTokenChangedResult = boolean | 'retry';

// load the firebase app on demand by putting it in a store
// this can then be used in derived stores for auth, firestore, and other services
export async function createContext(
  config: FirebaseOptions,
  options: {
    emailDomain: string;
    // https://www.google.com/recaptcha/admin
    // https://firebase.google.com/docs/app-check/web/recaptcha-provider
    recaptchaSiteKey?: string;
    // https://firebase.google.com/docs/auth/web/auth-state-persistence
    // authPersistence?: 'none' | 'session' | 'local';
    // TODO: This should be more customizable. Maybe the cache should be configured separately and
    // then provided.
    cacheResults?: boolean;
    onTokenChanged?: (token: string | undefined) => Promise<OnTokenChangedResult>;
    // These are for establishing dev-mode environments that talk to some or all prod services.
    forceProd?: boolean;
    forceProdAuth?: boolean;
    forceProdFirestore?: boolean;
    forceProdFunctions?: boolean;
    forceLogLevel?: LogLevel;
  },
) {
  const isDev = isDevContext(options?.forceProd);
  console.log(`createContext(${isDev ? 'DEV' : 'PROD'}, ${config.projectId})`);

  const { initializeApp, setLogLevel } = await import('firebase/app');
  if (isDev && options?.forceLogLevel) {
    setLogLevel(options.forceLogLevel);
  } else {
    setLogLevel(isDev ? 'info' : 'warn');
  }

  const app = initializeApp(config);
  const hostname = isDev && BROWSER ? window.location.hostname : '';

  // To try this (but it doesn't work):
  // - Uncomment recaptcha/enterprise.js script in app.html
  // - Switch launch.json to use localhost instead of millies.team.localhost
  // - Set this to true.
  const useAppCheck = false;
  if (useAppCheck && isPresent(options?.recaptchaSiteKey)) {
    const { initializeAppCheck, ReCaptchaEnterpriseProvider } = await import('firebase/app-check');
    // const { initializeAppCheck, ReCaptchaV3Provider } = await import('firebase/app-check');
    if (DEV) {
      // N.B. This requires crypto.randomUUID which means it only works with https or 'localhost'.
      // Therefore, local debug sessions must use localhost for this to work.
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      console.log(`initializeAppCheck(provider: ReCaptchaEnterpriseProvider)`);
    }
    const _appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(options.recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  const { getAuth, connectAuthEmulator, onIdTokenChanged } = await import('firebase/auth');

  const auth = getAuth(app);
  if (isDev && options?.forceProdAuth !== true) {
    connectAuthEmulator(auth, `http://${hostname}:9099`);
  }
  // if (options?.authPersistence) {
  //   await auth.setPersistence(options.authPersistence);
  // }

  if (isPresent(options.recaptchaSiteKey)) {
    // Optional.
    initializeRecaptchaConfig(auth)
      .then(() => {
        if (DEV) {
          console.log('Recaptcha initialized.');
        }
      })
      .catch((err) => {
        // Reasons this might fail:
        // - recaptchaSiteKey disagrees with:
        //   https://console.firebase.google.com/project/millies-catering/appcheck/apps
        //   Project > reCaptcha Enterprise > reCAPTCHA Enterprise site key
        // - getRecaptchaConfig response does not contain recaptchaKey
        //   Probably needs to be enabled with cli.sh firebase recaptcha.enable.
        // - Maybe roles/recaptchaenterprise.agent or missing identitytoolkit service account?
        console.error('Recaptcha initialization failed.', err);

        // IF you notice https://.../recaptcha/.../pat requests failing with 401, that request is
        // a probe and failures are expected and can be ignored.
        // https://www.googlecloudcommunity.com/gc/reCAPTCHA/reCAPTCHA-initialization-error-on-Safari-iOS-16/td-p/859376

        // If your sendVerificationCode URLS are failing with INVALID_APP_CREDENTIALS on localhost,
        // see: https://github.com/firebase/firebase-js-sdk/issues/8387#issuecomment-2253507927
        // The suggested workarounds are to use:
        //   127.0.0.1 (some people, including me, saw this fail as well)
        //   Fictional phone numbers (tried this, gave same error)
        //   Use a /etc/hosts alias
      });
  }

  const { getFirestore, connectFirestoreEmulator, DocumentReference, getDoc } = await import(
    'firebase/firestore'
  );
  const store = getFirestore(app);
  if (isDev && options?.forceProdFirestore !== true) {
    connectFirestoreEmulator(store, hostname, 8080);
  }

  const { getFunctions, connectFunctionsEmulator } = await import('firebase/functions');
  const func = getFunctions(app);
  if (isDev && options?.forceProdFunctions !== true) {
    connectFunctionsEmulator(func, hostname, 5001);
  }

  if (!DEV) {
    const { getAnalytics, logEvent } = await import('firebase/analytics');
    analytics_ = getAnalytics(app);
    logEvent_ = logEvent;
  }

  if (options?.cacheResults ?? false) {
    DescriptorCache.install(
      new FirestoreCache(DocumentReference, CollectionReference, getDoc, getDocs),
    );
  }

  fireContext.set(
    pare({
      app,
      auth,
      // recaptchaSiteKey: options?.recaptchaSiteKey,
      store,
      env: !isDev,
      emailDomain: options.emailDomain,
    }),
  );

  // If we're using session, we need to keep the server-side auth-state in sync with the client
  // this subscribes to the firebase client-side auth state and posts changes back to the server
  // to populate a firebase server-side cookie to mirror it
  if (BROWSER) {
    // Set to a dummy value so that either signed in or signed out trigger the first time.
    let cachedIdToken: string | undefined = 'uninitialized';
    let ignoreNext = false;
    const callback = options?.onTokenChanged;

    const handleSignOut = async (runCallback = true) => {
      if (isAbsent(cachedIdToken)) {
        return;
      }
      cachedIdToken = undefined;
      if (runCallback) {
        const result = await callback?.(undefined);
        checkEqual(result, false);
      }
      session.set({ user: null, claims: {} });
    };

    const handleIdTokenChanged = async (user: User | null) => {
      if (isAbsent(user)) {
        return handleSignOut();
      }
      let idToken = await user.getIdToken();
      if (idToken === cachedIdToken) {
        return;
      }
      let isSignedIn = true;
      const wasSignedIn = isAbsent(cachedIdToken);
      cachedIdToken = idToken;
      if (callback) {
        let result = await callback(idToken);
        if (result === 'retry') {
          ignoreNext = true;
          cachedIdToken = idToken = await user.getIdToken(true);
          result = await callback(idToken);
        }
        if (result === 'retry') {
          throw new Error('Sign in failed.');
        }
        isSignedIn = result;
      }
      if (!isSignedIn) {
        return handleSignOut(false);
      }
      if (!wasSignedIn && analytics_) {
        // TODO: Fix method.
        logEvent_?.(analytics_, 'login', { method: 'sms' });
      }
      session.set({ user, claims: parseTokenClaims(idToken) });
    };

    let tokenChangeId = 1;
    const debug = false;

    onIdTokenChanged(auth, (user) => {
      const id = tokenChangeId++;
      if (ignoreNext) {
        ignoreNext = false;
        if (debug) {
          console.log('onIdTokenChanged (ignored)', { id });
        }
        return;
      }
      if (debug) {
        console.log('onIdTokenChanged', {
          id,
          user: pick(user, 'uid', 'displayName', 'phoneNumber'),
        });
      }
      handleIdTokenChanged(user)
        .catch((err) => {
          if (debug) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            console.log('onIdTokenChanged error', { id, err });
          }
          return handleSignOut();
        })
        .finally(() => {
          if (debug) {
            console.log('onIdTokenChanged done', { id });
          }
        });
    });

    // onAuthStateChanged(auth, (user) => {
    //   console.log('onAuthStateChanged', { user: pick(user, 'uid', 'displayName', 'phoneNumber') });
    //   if (user) {
    //     // const created = fromHttp(user.metadata.creationTime);
    //     // // If account was created within the last 10 seconds, assume they just signed up for
    //     // // loyalty.
    //     // const signUpThresholdSeconds = 10;
    //     // if (
    //     //   analytics_ &&
    //     //   isPresent(created) &&
    //     //   created.diffNow('seconds').seconds <= signUpThresholdSeconds
    //     // ) {
    //     //   logEvent(analytics_, 'sign_up', { method: 'sms', acct_type: 'loyalty' });
    //     // }
    //     // ignore(
    //     //   user.getIdTokenResult().then((token) => {
    //     //     console.log('getIdTokenResult', { token: isPresent(token) });
    //     //     session.set(buildSession(user, token.claims));
    //     //     if (analytics_) {
    //     //       logEvent(analytics_, 'login', { method: 'sms' });
    //     //     }
    //     //   }),
    //     // );
    //     if (analytics_) {
    //       // TODO: Fix method.
    //       logEvent_?.(analytics_, 'login', { method: 'sms' });
    //     }
    //   }
    //   // if (!callback) {
    //   if (user) {
    //     // Async, we don't prevent login.
    //     ignore(
    //       user.getIdToken().then((idToken) => {
    //         session.set(buildSession(user, parseTokenClaims(idToken)));
    //       }),
    //     );
    //   } else {
    //     session.set(buildSession(user));
    //   }
    //   // }
    // });
  }
}
